import { Box, Drawer, Hidden, List, ListItem, ListItemIcon, ListItemText, Typography } from '@material-ui/core';
import { MoonTwoTone, SunIconTwoTone, Switch } from '@room-match/shared-ui-components';
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { ROUTES } from 'shared/constants/ROUTES';
import { SIDEBAR_ITEMS } from 'shared/constants/SIDEBAR_ITEMS';
import { setTheme as updateTheme } from 'shared/redux/slicers/theme.slicer';
import { useAppSelector } from 'shared/redux/store';
import { talentService } from 'shared/services/talentService';
import { ls } from 'shared/utils/ls';
import { useDebounce } from 'use-debounce';
import packageJson from '../../../package.json';
import { useMainListeItemStyle, useStyles } from './Sidebar.styles';
import ThemeBanner from './ThemeBanner/ThemeBanner';

type Props = {
  isMobileDrawerOpen?: boolean;
  handleMobileDrawer: () => void;
  isDrawerCollapse?: boolean;
  toggleDrawerCollapse: () => void;
};

const { updateMe } = talentService();

const Sidebar: React.FC<Props> = ({ isMobileDrawerOpen, handleMobileDrawer, isDrawerCollapse }) => {
  const queryClient = useQueryClient();
  const { app: appState, user: userState } = useAppSelector((state: any) => state);
  const { mutate: updateMeMutation } = updateMe();
  const { guardianUI } = appState.guardian;
  const { setLS, getLS } = ls();

  const [theme, setTheme] = useState<'light' | 'dark' | null>(null);
  const [themeVal] = useDebounce(theme, 500);
  const dispatch = useDispatch();

  const classes = useStyles();
  const history = useHistory();
  const listItemStyle = useMainListeItemStyle();
  const [selectedMenu, setSelectedMenu] = React.useState('');
  const [isFirsTime, setIsFirsTime] = useState<boolean>(false);

  const preferences = useMemo(() => {
    return userState?.user?.attributes.preferences;
  }, [userState]);

  const savedtheme = useMemo(() => {
    return userState?.user?.attributes.preferences?.theme || getLS('theme');
  }, [userState]);

  const handleToggleTheme = () => {
    if (theme === null || theme === savedtheme) return;
    updateMeMutation(
      {
        preferences: { ...preferences, theme },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(['me']);
        },
      },
    );
  };

  useEffect(() => {
    handleToggleTheme();
  }, [themeVal]);

  useEffect(() => {
    if (!savedtheme) {
      return setIsFirsTime(true);
    }
    if (theme === null && savedtheme) {
      setTheme(savedtheme);
      dispatch(updateTheme(savedtheme));
      setLS('theme', savedtheme);
    } else {
      dispatch(updateTheme(theme));
      setLS('theme', theme);
    }
  }, [theme, savedtheme]);

  useEffect(() => {
    const url = location.pathname.split('/').slice(1, 3).join('/');
    const item = SIDEBAR_ITEMS.find((f) => f.link?.includes(`/${url}`));
    setSelectedMenu(item?.name || '');

    history.listen((location) => {
      const url = location.pathname.split('/').slice(1, 3).join('/');
      const item = SIDEBAR_ITEMS.find((f) => f.link?.includes(`/${url}`));
      setSelectedMenu(item?.name || '');
    });

    console.log(url);
  }, [history, selectedMenu]);

  const handleBannerClick = () => {
    setIsFirsTime(false);
    setLS('theme', 'dark');
    updateMeMutation(
      {
        preferences: { ...preferences, theme: 'dark' },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(['me']);
        },
      },
    );
  };

  const drawer = (
    <div className={classes.drawerContainer}>
      <div className={classes.drawerHeader}>
        <Link to={ROUTES.TALENT.DASHBOARD}>
          {/* <CastingAppLogo style={{ height: '34px', width: '34px', marginLeft: '24px' }} /> */}
          <img
            src={'/casting-logo.png'}
            alt="File Logo"
            style={{
              objectFit: 'cover',
              alignSelf: 'center',
              height: '45px',
            }}
          />
        </Link>
      </div>
      <List className={classes.menuList}>
        {(guardianUI ? [SIDEBAR_ITEMS[0]] : SIDEBAR_ITEMS).map((item) => {
          return (
            <ListItem
              button
              key={item.name}
              selected={selectedMenu === item.name}
              disabled={!item.link}
              component={Link}
              to={item.link || ''}
              onClick={() => {
                setSelectedMenu(item.name);
                if (isMobileDrawerOpen) handleMobileDrawer();
              }}
              classes={listItemStyle}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.name} />
            </ListItem>
          );
        })}
      </List>

      <div
        onClick={() => {
          setSelectedMenu('');
        }}
      >
        <div>
          <Box className={classes.switchContent}>
            {theme === 'dark' || !savedtheme ? (
              <MoonTwoTone style={{ marginLeft: '7px' }} />
            ) : (
              <SunIconTwoTone style={{ marginLeft: '7px' }} />
            )}
            <Typography variant="body2">{theme === 'dark' || !savedtheme ? 'Studio' : 'Daylight'}</Typography>

            <Switch
              disabled={false}
              toggle={() => setTheme((t) => (t === 'dark' || !savedtheme ? 'light' : 'dark'))}
              checked={theme === 'dark' || !savedtheme}
            />
            <Typography variant="body2">v{packageJson.version}</Typography>
          </Box>
        </div>
      </div>
    </div>
  );
  return (
    <>
      <Hidden xsUp implementation="css">
        <Drawer
          variant="temporary"
          open={isMobileDrawerOpen}
          onClose={handleMobileDrawer}
          className={clsx(classes.drawer, {
            [classes.drawerOpen]: isMobileDrawerOpen,
            [classes.drawerClose]: !isMobileDrawerOpen,
          })}
          classes={{
            paper: clsx({
              [classes.drawerOpen]: isMobileDrawerOpen,
              [classes.drawerClose]: !isMobileDrawerOpen,
            }),
          }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          {drawer}
        </Drawer>
      </Hidden>
      <Hidden xsDown implementation="css">
        <Drawer
          variant="permanent"
          className={clsx(classes.drawer, {
            [classes.drawerOpen]: !isDrawerCollapse,
            [classes.drawerClose]: isDrawerCollapse,
          })}
          classes={{
            paper: clsx({
              [classes.drawerOpen]: !isDrawerCollapse,
              [classes.drawerClose]: isDrawerCollapse,
            }),
          }}
        >
          {drawer}
        </Drawer>
      </Hidden>
      {isFirsTime ? <ThemeBanner isFirsTime={isFirsTime} handleBannerClick={handleBannerClick} /> : <React.Fragment />}
    </>
  );
};

export default Sidebar;
